import { flatten, get, isNil, merge } from "lodash"

export const filterSearchResult = (feature) => {
  const { feature_type, properties } = feature

  // Hide occupants without anchor, unit, or kiosk unless in maintenance mode
  const isUnlocated =
    !properties?.anchor && !properties?.kiosk && !properties?.unit
  const isNotMaintenanceMode = !properties.is_maintenance
  if (feature_type === "occupant" && isUnlocated && isNotMaintenanceMode)
    return false

  return true
}

export const filterSuggestedSearchResult = (feature) => {
  const { feature_type, properties } = feature

  const hasLocated = properties?.anchor || properties?.kiosk || properties?.unit

  return (
    feature_type === "occupant" && (hasLocated || properties.is_maintenance)
  )
}

export const getTaxonomyFunction =
  (field: string) =>
  ({ id, properties }) => {
    try {
      const targetFieldArr = get(properties, field, [])
      const categoryLocaleObjs = targetFieldArr.map((category) =>
        get(category, "properties.name")
      )
      return categoryLocaleObjs?.length
        ? flatten(
            categoryLocaleObjs.map((localizedName) =>
              Object.values(localizedName)
            )
          )
        : ""
    } catch (error) {
      console.log("error : while getting taxonomy name from feature id : ", id)
    }
  }

const getFeatureName =
  (field: string) =>
  ({ properties }) => {
    const targetField = get(properties, field)
    return targetField ? Object.values(targetField) : ""
  }

const KEY_CONFIGS = {
  name: {
    name: "name",
    weight: 4,
    getFn: getFeatureName("name"),
  },
  short_name: {
    name: "short_name",
    weight: 4,
    getFn: getFeatureName("short_name"),
  },
  category: {
    name: "category",
    weight: 0.25,
    getFn: ({ properties }) => properties?.category || "",
  },
  groups: {
    name: "groups",
    getFn: getTaxonomyFunction("groups"),
    weight: 0.4,
  },
  local_categories: {
    name: "local_categories",
    getFn: getTaxonomyFunction("local_categories"),
    weight: 0.25,
  },
  "properties.keywords": {
    name: "properties.keywords",
    weight: 0.25,
  },
  feature_type: {
    name: "feature_type",
    weight: 1,
  },
  "kiosk.name": {
    name: "kiosk.name",
    weight: 0.25,
    getFn: getFeatureName("kiosk.properties.name"),
  },
  "unit.name": {
    name: "unit.name",
    weight: 0.25,
    getFn: getFeatureName("unit.properties.name"),
  },
  "section.name": {
    name: "section.name",
    weight: 0.25,
    getFn: getFeatureName("section.properties.name"),
  },
}

export const createSearchConfig = (config) => {
  const keys = get(config, "keys")
  const featureTypes = get(config, "featureTypes")

  if (isNil(keys) || isNil(featureTypes))
    throw new Error(
      "The search configuration must include specified 'keys' and 'featureTypes'."
    )

  return {
    keys: keys
      .filter(({ name }) => KEY_CONFIGS[name])
      .map((k) => merge(KEY_CONFIGS[k.name], k)),
    featureTypes,
  }
}

const COMMON_OR_FIELDS = [
  "name",
  "short_name",
  "category",
  "properties.keywords",
  "groups",
  "local_categories",
] as const

const OCCUPANT_EXTRA_FIELDS = [
  "kiosk.name",
  "unit.name",
  "section.name",
] as const

// Fields whose Fuse extended-search clause must use the { $path, $val } shape
// instead of the default { [field]: value } shape.
const PATH_FIELDS = new Set<string>(["properties.keywords"])

const toFuseClause = (field: string, value: string) =>
  PATH_FIELDS.has(field) ? { $path: [field], $val: value } : { [field]: value }

export const createSearchPattern = (value) => {
  const commonOr = COMMON_OR_FIELDS.map((f) => toFuseClause(f, value))

  return {
    "=amenity": {
      $and: [{ feature_type: "=amenity" }, { category: `=${value}` }],
    },
    "=occupant": {
      $and: [
        { feature_type: "=occupant" },
        {
          $or: [
            ...commonOr,
            ...OCCUPANT_EXTRA_FIELDS.map((f) => toFuseClause(f, value)),
          ],
        },
      ],
    },
    "=occupant | =amenity": {
      $and: [{ feature_type: "=occupant | =amenity" }, { $or: commonOr }],
    },
    "=occupant | =amenity | =section": {
      $and: [
        { feature_type: "=occupant | =amenity | =section" },
        { $or: commonOr },
      ],
    },
  }
}
